/**
 * fireflies.js — drifting, blinking fireflies for green-season nights.
 *
 * A single THREE.Points cloud hovering around the tree, lit by a small additive
 * shader: each fly wanders on its own slow sine paths and blinks on its own phase,
 * so the swarm twinkles asynchronously. Unlike the starfield/aurora (far on the sky
 * dome) these live in the garden near the foliage, but they share the same night
 * recipe — additive, depthWrite off — and the same eased on/off fade.
 *
 * Built once at boot, added to the scene by main.js's initSky(); updateFireflies()
 * is called each frame from animate(). Shown only when ALL hold:
 *   • enabled — Settings ▸ Zen Garden ▸ Fireflies (setFirefliesEnabled), and
 *   • night  — the starfield's sun-height fade (nightVis), and
 *   • not winter — parameters.leafType !== 'none' (no fireflies over snow), and
 *   • not autumn — parameters.foliageColor !== 'autumn'.
 * The gate is eased inside updateFireflies so the swarm glides in/out.
 */

import * as THREE from 'three';

const FIREFLIES = {
  count:       70,
  radius:      220,   // horizontal spread around the trunk
  yMin:        15,    // hover from just above the ground...
  yMax:        200,   // ...up around the canopy
  wanderXZ:    20,    // drift amplitude (horizontal)
  wanderY:     14,    // drift amplitude (vertical)
  sizeMin:     7,
  sizeMax:     15,
  maxOpacity:  0.9,   // additive cap (the blink already keeps most flies dim)
  fadeRate:    1.2,   // season-presence ease speed (≈0.8s in/out)
};

// Warm yellow-green glow, slightly brightened for additive blending.
const FLY_COLORS = [
  new THREE.Color(0xbfff66),
  new THREE.Color(0xeaff7a),
  new THREE.Color(0xa8ff8c),
  new THREE.Color(0xfff0a0),
];

const vertexShader = /* glsl */`
  attribute vec3  aPhase;  // x/y/z wander phase offsets
  attribute vec2  aBlink;  // x: blink phase, y: blink speed
  attribute float aSize;
  attribute vec3  aColor;
  uniform float uTime;
  varying float vBright;
  varying vec3  vColor;
  void main() {
    // Slow independent wander on each axis.
    vec3 p = position;
    p.x += sin(uTime * 0.5 + aPhase.x) * ${FIREFLIES.wanderXZ.toFixed(1)};
    p.z += cos(uTime * 0.4 + aPhase.z) * ${FIREFLIES.wanderXZ.toFixed(1)};
    p.y += sin(uTime * 0.3 + aPhase.y) * ${FIREFLIES.wanderY.toFixed(1)};

    // Blink: a sharpened sine so each fly flares briefly then goes dark.
    float b = 0.5 + 0.5 * sin(uTime * aBlink.y + aBlink.x);
    vBright = pow(b, 3.0);
    vColor = aColor;

    vec4 mv = modelViewMatrix * vec4(p, 1.0);
    gl_PointSize = aSize * (300.0 / -mv.z); // perspective size attenuation
    gl_Position = projectionMatrix * mv;
  }
`;

const fragmentShader = /* glsl */`
  uniform float uOpacity;
  varying float vBright;
  varying vec3  vColor;
  void main() {
    // Soft round glow with a brighter core.
    float d = length(gl_PointCoord - vec2(0.5));
    if (d > 0.5) discard;
    float glow = smoothstep(0.5, 0.0, d);
    glow *= glow;
    gl_FragColor = vec4(vColor, glow * vBright * uOpacity);
  }
`;

let mesh = null;
let presence = 0;       // eased gate, 0 (hidden) → 1 (showing)
let enabled = false;    // Settings ▸ Zen Garden ▸ Fireflies (master switch)

/** Settings toggle: master on/off for the swarm (still gated by night + season). */
export function setFirefliesEnabled(on) { enabled = !!on; }

/**
 * Build the firefly swarm. Call once; add the returned Points to the scene.
 * @returns {THREE.Points}
 */
export function createFireflies() {
  const n = FIREFLIES.count;
  const positions = new Float32Array(n * 3);
  const phases    = new Float32Array(n * 3);
  const blinks    = new Float32Array(n * 2);
  const sizes     = new Float32Array(n);
  const colors    = new Float32Array(n * 3);

  for (let i = 0; i < n; i++) {
    // Disc distribution around the trunk (sqrt for even area density).
    const r = Math.sqrt(Math.random()) * FIREFLIES.radius;
    const a = Math.random() * Math.PI * 2;
    positions[i * 3]     = Math.cos(a) * r;
    positions[i * 3 + 1] = FIREFLIES.yMin + Math.random() * (FIREFLIES.yMax - FIREFLIES.yMin);
    positions[i * 3 + 2] = Math.sin(a) * r;

    phases[i * 3]     = Math.random() * Math.PI * 2;
    phases[i * 3 + 1] = Math.random() * Math.PI * 2;
    phases[i * 3 + 2] = Math.random() * Math.PI * 2;

    blinks[i * 2]     = Math.random() * Math.PI * 2;       // phase
    blinks[i * 2 + 1] = 0.8 + Math.random() * 1.6;          // speed

    sizes[i] = FIREFLIES.sizeMin + Math.random() * (FIREFLIES.sizeMax - FIREFLIES.sizeMin);

    const c = FLY_COLORS[Math.floor(Math.random() * FLY_COLORS.length)];
    colors[i * 3]     = c.r;
    colors[i * 3 + 1] = c.g;
    colors[i * 3 + 2] = c.b;
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute('aPhase',   new THREE.BufferAttribute(phases, 3));
  geometry.setAttribute('aBlink',   new THREE.BufferAttribute(blinks, 2));
  geometry.setAttribute('aSize',    new THREE.BufferAttribute(sizes, 1));
  geometry.setAttribute('aColor',   new THREE.BufferAttribute(colors, 3));

  const material = new THREE.ShaderMaterial({
    vertexShader,
    fragmentShader,
    uniforms: { uTime: { value: 0 }, uOpacity: { value: 0 } },
    transparent: true,
    blending:    THREE.AdditiveBlending,
    depthWrite:  false,
    depthTest:   true,
  });

  mesh = new THREE.Points(geometry, material);
  mesh.frustumCulled = false; // the swarm wanders past its initial bounds
  mesh.visible = false;
  return mesh;
}

/**
 * Drive the fireflies each frame from animate().
 * @param {number}  timeSeconds - performance.now()/1000 (wander + blink clock)
 * @param {number}  dt          - frame delta in seconds (for the season ease)
 * @param {number}  nightVis    - 0 (day) → 1 (full night); the starfield's fade
 * @param {boolean} allowed     - green season: not winter and not autumn
 */
export function updateFireflies(timeSeconds, dt, nightVis, allowed) {
  if (!mesh) return;
  const target = (enabled && allowed) ? 1 : 0;
  presence += (target - presence) * Math.min(1, dt * FIREFLIES.fadeRate);

  const opacity = nightVis * presence * FIREFLIES.maxOpacity;
  mesh.material.uniforms.uTime.value = timeSeconds;
  mesh.material.uniforms.uOpacity.value = opacity;
  mesh.visible = opacity > 0.002;
}
